import type {
  DraggableListQuestion,
  MultipleChoiceQuestion,
  Question,
  RatingQuestion,
  ShortTextQuestion,
  TextQuestion,
} from '@shared/domain/form/index.js';
import {
  QuestionType,
  QuestionValidationRules,
} from '@shared/domain/form/index.js';
import { RatingAspect } from '@shared/domain/review/index.js';

export const BASIC_REVIEW_FORM_FIELDS: Question[] = [
  {
    name: 'dealArea',
    options: [
      'Customer Success',
      'Engineering',
      'Finance',
      'HR',
      'Legal',
      'Marketing',
      'Operations',
      'Product',
      'Sales',
      'IT',
      'Security',
    ],
    question: 'Which part of the organization did you sell to?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'aiInvolved',
    options: [
      'Yes',
      'No',
    ],
    question: 'Does your product have any AI applications to it?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'dealSize',
    options: [
      'Not Disclosable',
      '0 - 25k',
      '25k - 50k',
      '50k - 100k',
      '100k - 250k',
      '250k - 500k',
      '500k - 1M',
      '1MM+',
    ],
    question: 'What was the annual contract value of the deal?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,

  {
    name: 'dealType',
    options: [
      'New Business',
      'Renewal',
      'Expansion/Upsell',
    ],
    question: 'What type of deal is this?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'dealLength',
    options: [
      'Monthly',
      'Annual',
      'Multi-year',
    ],
    question: 'Term of the deal?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'dealClose',
    options: [
      'Yes',
      'No',
    ],
    question: 'Did you win the deal?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'dealDate',
    options: Array.from(
      { length: 5 },
      (_, index) => {
        return `${new Date().getFullYear() - index}`;
      },
    ),
    question: 'When was the deal sold?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'dealRfp',
    options: [
      'Yes',
      'No',
    ],
    question: 'Did this deal include an RFP?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,

  {
    helperText:
      'Please do not include specific technologies, confidential tools or named individuals.',
    name: 'commentBuyerWell',
    question: 'What did the buyer do well?',
    tooltipText:
      'Include specific examples of actions or behaviors that contributed positively to the sales process.',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
      QuestionValidationRules.MINIMUM_CHARACTERS,
    ],
  } as TextQuestion,
  {
    helperText:
      'Please do not include specific technologies, confidential tools or named individuals.',
    name: 'commentBuyerBetter',
    question: 'What could the buyer have done better?',
    tooltipText:
      'Include specific examples of actions or behaviors that contributed negatively to the sales process.',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
      QuestionValidationRules.MINIMUM_CHARACTERS,
    ],
  } as TextQuestion,
  {
    helperText:
      'Please do not include specific technologies, confidential tools or named individuals.',
    name: 'commentWinLose',
    question:
      'Was there anything unique or different about this sales cycle that caused you to win/lose?',
    tooltipText:
      'What are the largest specific factors that you believe influenced the outcome?  Include things like competitive pressure, willingness to negotiate price, important relationships built, etc.',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
      QuestionValidationRules.MINIMUM_CHARACTERS,
    ],
  } as TextQuestion,
  {
    helperText:
      'Do not disclose specific internal systems, tools, data or personnel.',
    name: 'commentAdvice',
    question: 'What advice do you have for the next seller?',
    tooltipText:
      'Include things like key details of the account structure, advice on communication style, known roadblocks to avoid and common objections you hit.',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
      QuestionValidationRules.MINIMUM_CHARACTERS,
    ],
  } as TextQuestion,
  {
    name: 'transparency',
    question: 'Transparency',
    responseName: RatingAspect.TRANSPARENCY,
    tooltipText:
      'Was the buyer open about the steps that needed to take place in their buying process?',
    type: QuestionType.RATING,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as RatingQuestion,
  {
    name: 'clarity',
    question: 'Clarity of Needs',
    responseName: RatingAspect.CLARITY,
    tooltipText:
      'Did the buyer clearly articulate what they needed your solution to solve in order to make a purchase?',
    type: QuestionType.RATING,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as RatingQuestion,
  {
    name: 'understanding',
    question: 'Technical Competence',
    responseName: RatingAspect.UNDERSTANDING,
    tooltipText: 'Was the buyer able to understand how the technology worked?',
    type: QuestionType.RATING,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as RatingQuestion,
  {
    name: 'responsiveness',
    question: 'Responsiveness',
    responseName: RatingAspect.RESPONSIVENESS,
    tooltipText:
      'Did the buyer respond back to your emails and phone calls in a timely fashion?',
    type: QuestionType.RATING,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as RatingQuestion,
];

export const ENTRY_STRATEGY_FORM_FIELDS: Question[] = [
  {
    name: 'entryAttentionSource',
    options: [
      'Cold outreach',
      'Referral',
      'Event',
      'Inbound lead',
      'SDR generated',
      'Other',
    ],
    question: 'How did you first get the attention of this account?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'entryAttentionSource',
        value: 'Other',
      },
    ],
    name: 'entryAttentionOtherComment',
    question: "Please describe the 'Other' way you first got attention.",
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
      QuestionValidationRules.MINIMUM_CHARACTERS,
    ],
  } as TextQuestion,
  {
    name: 'breakthroughChannel',
    options: [
      'Email',
      'Phone',
      'LinkedIn',
      'Text/SMS',
      'Other',
    ],
    question: 'What channel ultimately got you the breakthrough conversation?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'breakthroughChannel',
        value: 'Other',
      },
    ],
    name: 'breakthroughChannelOtherComment',
    question: "Please describe the 'Other' breakthrough channel.",
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    name: 'outreachPainPointsComment',
    question:
      'What pain points did you focus on in your outreach that resonated most?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    name: 'externalProofLeverageComment',
    question:
      'Did you leverage any external proof (case studies, benchmarks, ROI framing) that made a difference?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    name: 'adviceFirstStepComment',
    question:
      'If another AE were targeting this account type, what’s the first thing you’d tell them to do?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    name: 'avoidDoingComment',
    question:
      'What would you avoid doing if you had to break into this account again?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
];

export const PURCHASE_DETAILS_FORM_FIELDS = [
  {
    name: 'economicBuyerName',
    question: 'What was the title of the economic buyer?',
    type: QuestionType.SHORT_TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as ShortTextQuestion,
  {
    name: 'championName',
    question: 'What was the title of your champion?',
    type: QuestionType.SHORT_TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as ShortTextQuestion,
  {
    name: 'championEffectiveness',
    options: [
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
    ],
    question:
      'On a scale of 1 - 10 (where 10 is most effective), how effective was your champion at getting the deal done?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'detractorExistence',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question: 'Were there any detractors to the deal?',
    required: true,
    type: QuestionType.MULTIPLE_CHOICE,
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'detractorExistence',
        value: 'Yes',
      },
    ],
    helperText: 'Use titles, not names. You can add more than one.',
    name: 'detractorTitles',
    question: 'What were the titles of the detractors?',
    required: true,
    type: QuestionType.DRAGGABLE_LIST,
  } as DraggableListQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'detractorExistence',
        value: 'Yes',
      },
    ],
    name: 'detractorResistanceComment',
    question: 'How did detractor resistance show up and what impact did it have?',
    required: true,
    type: QuestionType.TEXT,
  } as TextQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'detractorExistence',
        value: 'Yes',
      },
    ],
    name: 'detractorStrategiesComment',
    question: 'What strategies helped (or failed) to overcome detractor objections?',
    required: true,
    type: QuestionType.TEXT,
  } as TextQuestion,
  {
    name: 'definedBudget',
    options: [
      'Yes',
      'No',
      'Unknown',
    ],
    question: 'Was there a defined budget for this purchase?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'salesCycleLength',
    options: [
      '< 1 Month',
      '1 - 3 Months',
      '3 - 6 Months',
      '6 - 12 Months',
      '12+ Months',
    ],
    question: 'How long was the sales cycle from start to finish?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'buyerFiscalYearEnd',
    options: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
      'Unknown',
    ],
    question: "When does the buyer's fiscal year end?",
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'paymentCycle',
    options: [
      'Monthly',
      'Quarterly',
      'Annually',
      'Other',
      'N/A',
    ],
    question:
      'Did the customer agree to pay monthly, quarterly, annually or other?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'championComment',
    question: 'Any notes on how to best work with this champion?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
];

export const EVALUATION_FORM_FIELDS = [
  {
    name: 'buyerReplacingVendor',
    options: [
      'Yes',
      'No',
      'Unknown',
    ],
    question: 'Was the buyer replacing an incumbent vendor?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'submitRoi',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question:
      'Were you required to submit an ROI or other business justification to win this deal?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'requireTrial',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question: 'Did the customer request a trial?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'requireTrial',
        value: 'Yes',
      },
    ],
    name: 'trialAccommodated',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question: 'Was a trial accommodated?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'requireTrial',
        value: 'Yes',
      },
      {
        field: 'trialAccommodated',
        value: 'Yes',
      },
    ],
    name: 'trialLength',
    options: [
      '< 1 Week',
      '2-4 Weeks',
      '2-3 Months',
      '3-4 Months',
      '5-6 Months',
      '>6 Months',
    ],
    question: 'How long was the trial?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'noDecisionMakers',
    options: [
      '1',
      '2-3',
      '4-5',
      '5+',
    ],
    question:
      'How many decision makers were involved in the evaluation process?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'durationSecurityReview',
    options: [
      'Not Required',
      '< Week',
      '2 Weeks',
      '3 Weeks',
      '4+ Weeks',
      'N/A',
    ],
    question: 'What was the duration of the security review?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'securityQuestionnaire',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question:
      'Did the Security review process include a Standard Intelligence Gathering Questionnaire?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'durationSecurityReview',
        notValues: [
          'N/A',
          'Not Required',
        ],
      },
    ],
    name: 'securityDifficulty',
    options: [
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      'N/A',
    ],
    question:
      'On a scale of 1 - 10 (where 10 is most difficult), how difficult was it to pass the security review?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,

  {
    name: 'criteriaArticulatedByBuyer',
    options: [
      'Yes',
      'No',
    ],
    question: 'Was the evaluation criteria clearly articulated by the buyer?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,

  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'criteriaArticulatedByBuyer',
        value: 'No',
      },
    ],
    helperText:
      'Do not include internal tool names or scoring systems. Keep answers general (e.g., ‘needed more alignment across teams’).',
    name: 'articulatingCriteriaComment',
    question:
      'What could the buyer have done better when articulating the evaluation criteria?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    helperText:
      'Avoid details on internal scoring, decision matrices, naming specific tools or references to specific terms.',
    name: 'criteriaChangeComment',
    question: 'Did the buyer change the evaluation criteria, and if so, how?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    conditionalShowRules: [
      {
        field: 'requireTrial',
        value: 'Yes',
      },
      {
        field: 'trialAccommodated',
        value: 'No',
      },
    ],
    name: 'negotiateTrialComment',
    question:
      'Please share how you were able to negotiate the customer out of the request for a trial?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'durationSecurityReview',
        notValues: [
          'N/A',
          'Not Required',
        ],
      },
    ],
    name: 'securityRequirementsComment',
    question:
      'Are there any specific requirements you must meet to pass the security review?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'submitRoi',
        value: 'Yes',
      },
    ],
    helperText:
      'Only describe categories of KPIs (e.g., ‘reduced support tickets,’ ‘compliance improvement’). Do not include dollar values or named metrics.',
    name: 'roiJustificationKPIsComment',
    question:
      'What KPI’s or outcomes were used to justify your ROI or business justification submission?',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    name: 'approversList',
    placeholder: 'Once added, a list will populate below',
    question:
      'Please add all approvers titles (no names) in chronological order:',
    type: QuestionType.DRAGGABLE_LIST,
  } as DraggableListQuestion,
  {
    helperText:
      'Only share general platform categories (e.g., ‘CRM,’ ‘Payroll,’ ‘ERP’). Do not list product names unless they are public or widely known.  You can reorder the list after adding items.',
    name: 'platFormList',
    placeholder: 'Once added, a list will populate below',
    question:
      'Please add all platforms you were required to integrate with as part of the process:',
    type: QuestionType.DRAGGABLE_LIST,
  },
];

export const LEGAL_FORM_FIELDS = [
  {
    name: 'legalReviewLength',
    options: [
      'Not Required',
      '1-2 Weeks',
      '3-4 Weeks',
      '1-3 Months',
      '3-6 Months',
      '>6 Months',
      'N/A',
    ],
    question: 'What was the duration of the legal review?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'legalReviewLength',
        notValues: [
          'N/A',
          'Not Required',
        ],
      },
    ],
    name: 'legalReviewDifficulty',
    options: [
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      'N/A',
    ],
    question:
      'On a scale of 1 - 10 (where 10 is most difficult), how difficult was it to pass the legal review?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'paperworkOption',
    options: [
      'Our contracting paperwork',
      'Their contracting paperwork',
      'N/A',
    ],
    question:
      'Did the customer agree to your companies contracting paperwork or did they require use of their own?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'customerAskLiability',
    options: [
      'Inline',
      'Above',
      'Below',
    ],
    question:
      'Was the requested Limitation of Liability by the buyer inline with expectations, above expectations or below expectations?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'endAskLiability',
    options: [
      'Inline',
      'Above',
      'Below',
    ],
    question:
      'Was the agreed upon Limitation of Liability by the buyer inline with expectations, above expectations or below expectations?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'liabilityExistence',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question:
      'Were there any carve outs and/or supercaps to the LoL/Indemnification agreed upon?',
    type: QuestionType.MULTIPLE_CHOICE,
  } as MultipleChoiceQuestion,
  {
    helperText:
      'Avoid quoting or summarizing clauses. Share only general topics (e.g., ‘IP indemnification pushback’) and tactics (‘offered limited warranty clause’).',
    name: 'notableGenComment',
    question:
      "What were the legal team's main points of contention, and how did you mitigate/address them?",
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        compareTo: 'customerAskLiability',
        comparison: 'lessThan',
        field: 'endAskLiability',
      },
    ],
    helperText:
      'Describe your approach, not their terms. E.g., ‘offered higher coverage cap on cyber insurance,’ not ‘they dropped LoL to 3x.',
    name: 'liabilityStrategiesComment',
    question:
      'Please share any details or strategies that helped you negotiate a lower Liability/Indemnification request.',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
];

export const NEGOTIATION_FORM_FIELDS = [
  {
    name: 'noticeSelection',
    options: [
      'Yes',
      'No',
    ],
    question: 'Did you receive notice that you were the selected vendor?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'noticeSelection',
        value: 'Yes',
      },
    ],
    name: 'noticeHonored',
    options: [
      'Yes',
      'No',
    ],
    question: 'Was this notice honored?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,

  {
    name: 'procurementThreshold',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question: 'Was procurement involved in this negotiation?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'procurementFairness',
    options: [
      'Yes',
      'No',
    ],
    question: 'Did procurement treat you fair and honest?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,

  {
    name: 'procurementTactics',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question:
      'Did procurement/buyer use any tactics to force you to take unfavorable terms and/or additional discounts?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'competitiveBids',
    options: [
      'Yes',
      'No',
    ],
    question: 'Did procurement require competitive bids?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'fundsReleased',
    options: [
      'Yes',
      'No',
      'N/A',
    ],
    question: 'Was a business case required to get funds released?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'discountGiven',
    options: [
      'Yes',
      'No',
    ],
    question: 'Did you give a discount to win the deal?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    conditionallyShow: true,
    conditionalShowRules: [
      {
        field: 'discountGiven',
        value: 'Yes',
      },
    ],
    name: 'discountRange',
    options: [
      'Above',
      'At',
      'Below',
    ],
    question: 'Was the discount above, at or below your typical range?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    helperText:
      'Do not include clause language or negotiation quotes. Focus on what worked for you.',
    name: 'negotiationTipsComment',
    question: 'Any tips to other sellers to help negotiate with this customer?',
    tooltipText:
      'Include specific negotiation leverage you used, how you tackled key objections, key factors the influenced decision making or any insight into relationship dynamics that impacted the deal.',
    type: QuestionType.TEXT,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as TextQuestion,
];

export const RFP_FORM_FIELDS = [
  {
    name: 'rfpWin',
    options: [
      'Yes',
      'No',
    ],
    question: 'Did you win the RFP?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'rfpInvolvement',
    options: [
      'Yes',
      'No',
    ],
    question: 'Were you involved in the RFP process?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'rfpLength',
    options: [
      '< 1 Month',
      '1 - 3 Months',
      '3 - 6 Months',
      '6+ Months',
    ],
    question: 'How long was the RFP from initiation to final decision?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
  {
    name: 'acceptanceFormat',
    options: [
      'Email',
      'Procurement Portal',
      'Vendor Registration System',
      'PDF Upload via Link',
      'Word Document Attachment',
      'Excel Template Submission',
      'Online Form',
      'E-Sourcing Platform',
      'In-Person Presentation or Live Pitch',
      'Other',
    ],
    question: 'Through what format did the buyer accept submission?',
    type: QuestionType.MULTIPLE_CHOICE,
    validationRules: [
      QuestionValidationRules.REQUIRED,
    ],
  } as MultipleChoiceQuestion,
];
